(() => {
  const OPEN_CLASS = 'is-open';
  const BODY_OPEN_CLASS = 'account-panel--open';
  const DESKTOP_QUERY = window.matchMedia('(min-width: 750px)');
  const KWIKPASS_DRAWER_SELECTOR = '#kwikpass-drawer-desktop, #kwikpass-drawer-mobile';

  /**
   * Returns focusable nodes inside a container for initial-focus placement.
   */
  const getFocusableElements = (container) =>
    container.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

  const setupAccountPanel = (panel) => {
    const panelId = panel.getAttribute('id');
    if (!panelId) return;

    const toggles = Array.from(
      document.querySelectorAll(`[data-account-panel-toggle][aria-controls="${panelId}"]`)
    );
    if (!toggles.length) return;

    const surface = panel.querySelector('.account-panel__surface');
    const closeTargets = panel.querySelectorAll('[data-account-panel-close]');
    const navLinks = panel.querySelectorAll('[data-account-panel-link]');
    let lastTrigger = null;

    /**
     * Align desktop dropdown to the account icon so it opens in the expected spot.
     */
    const setDesktopPosition = (trigger) => {
      if (!DESKTOP_QUERY.matches || !trigger || !surface) return;

      const rect = trigger.getBoundingClientRect();
      const right = Math.max(16, window.innerWidth - rect.right);
      const top = Math.max(16, rect.bottom + 8);
      const maxWidth = Math.min(340, window.innerWidth - 32);

      panel.style.setProperty('--account-panel-right', `${right}px`);
      panel.style.setProperty('--account-panel-top', `${top}px`);
      surface.style.maxWidth = `${maxWidth}px`;
    };

    const openPanel = (trigger) => {
      if (!panel.hidden) return;

      lastTrigger = trigger || toggles[0];
      setDesktopPosition(lastTrigger);
      panel.hidden = false;

      window.requestAnimationFrame(() => {
        panel.classList.add(OPEN_CLASS);
      });

      toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'true'));
      document.body.classList.add(BODY_OPEN_CLASS);

      const focusable = surface ? getFocusableElements(surface) : [];
      if (focusable.length) focusable[0].focus();
    };

    const closePanel = (restoreFocus = true) => {
      if (panel.hidden) return;

      panel.classList.remove(OPEN_CLASS);
      toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
      document.body.classList.remove(BODY_OPEN_CLASS);

      window.setTimeout(() => {
        if (!panel.classList.contains(OPEN_CLASS)) {
          panel.hidden = true;
        }
      }, 220);

      if (restoreFocus && lastTrigger) {
        lastTrigger.focus();
      }
    };

    toggles.forEach((toggle) => {
      toggle.addEventListener('click', (event) => {
        event.preventDefault();

        if (panel.hidden) {
          openPanel(toggle);
        } else {
          closePanel(false);
        }
      });
    });

    closeTargets.forEach((target) => {
      target.addEventListener('click', () => closePanel());
    });

    navLinks.forEach((link) => {
      link.addEventListener('click', () => closePanel(false));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !panel.hidden) {
        event.preventDefault();
        closePanel();
      }
    });

    window.addEventListener('resize', () => {
      if (!panel.hidden) {
        setDesktopPosition(lastTrigger);
      }
    });

    DESKTOP_QUERY.addEventListener('change', (event) => {
      if (!event.matches) {
        closePanel(false);
      }
    });
  };

  /**
   * Adds the account destination to KwikPass' injected signed-in menu.
   * KwikPass can rebuild this menu, so the enhancement is idempotent.
   */
  const addKwikpassAccountLink = (drawer) => {
    const list = drawer.querySelector('ul');
    const header = document.querySelector('.header[data-account-url]');

    if (!list || !header || list.querySelector('[data-ratniva-kwikpass-account]')) return;

    const accountUrl = header.dataset.accountUrl;
    const accountLabel = header.dataset.accountLabel || 'My Account';
    if (!accountUrl) return;

    const link = document.createElement('a');
    link.href = accountUrl;
    link.className = 'ratniva-kwikpass-account-link';
    link.dataset.ratnivaKwikpassAccount = '';

    const content = document.createElement('p');
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('width', '20');
    icon.setAttribute('height', '20');
    icon.setAttribute('fill', 'none');
    icon.setAttribute('stroke', 'currentColor');
    icon.setAttribute('stroke-width', '1.7');
    icon.setAttribute('aria-hidden', 'true');

    const head = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    head.setAttribute('cx', '12');
    head.setAttribute('cy', '8');
    head.setAttribute('r', '3.5');

    const shoulders = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    shoulders.setAttribute('d', 'M5.5 20c.7-4 3-6 6.5-6s5.8 2 6.5 6');
    shoulders.setAttribute('stroke-linecap', 'round');

    const label = document.createElement('span');
    label.textContent = accountLabel;
    icon.append(head, shoulders);
    content.append(icon, label);
    link.append(content);

    link.addEventListener('click', (event) => {
      if (typeof window.handleShopifyLogin !== 'function') return;

      event.preventDefault();
      window.handleShopifyLogin(event, accountUrl);
    });

    list.prepend(link);
  };

  const enhanceKwikpassMenus = (root = document) => {
    if (root instanceof Element && root.matches(KWIKPASS_DRAWER_SELECTOR)) {
      addKwikpassAccountLink(root);
    }

    root.querySelectorAll?.(KWIKPASS_DRAWER_SELECTOR).forEach(addKwikpassAccountLink);
  };

  document.addEventListener('DOMContentLoaded', () => {
    const panels = document.querySelectorAll('[data-account-panel]');
    panels.forEach((panel) => setupAccountPanel(panel));

    enhanceKwikpassMenus();

    const kwikpassObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) enhanceKwikpassMenus(node);
        });
      });
    });

    kwikpassObserver.observe(document.body, { childList: true, subtree: true });
  });
})();
